/**
 * Seedable 3D Perlin noise with utility layers:
 *   FBM, ridge, billow, domain-warp, cellular / Voronoi.
 *
 * Hash-based gradient noise — no permutation table needed,
 * so every instance is trivially seedable.
 */
export class Noise {
  constructor(seed = 0) {
    this.seed = seed | 0;
  }

  /* ── core hash & gradient ─────────────────────── */

  /** Integer hash combining grid position + seed (murmur-ish). */
  _hash(ix, iy, iz) {
    let h = this.seed;
    h = (h ^ Math.imul(ix, 374761393)) | 0;
    h = (h ^ Math.imul(iy, 668265263)) | 0;
    h = (h ^ Math.imul(iz, 1274126177)) | 0;
    h = Math.imul(h ^ (h >>> 16), 2246822519);
    h = Math.imul(h ^ (h >>> 13), 3266489917);
    return (h ^ (h >>> 16)) >>> 0;
  }

  /** Map hash → one of 12 gradient directions, dot with (x,y,z). */
  _grad(hash, x, y, z) {
    const h = hash & 15;
    const u = h < 8 ? x : y;
    const v = h < 4 ? y : (h === 12 || h === 14 ? x : z);
    return ((h & 1) ? -u : u) + ((h & 2) ? -v : v);
  }

  /** Quintic fade for smooth second-derivative. */
  _fade(t) {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  /* ── 3D Perlin noise  ─ returns approx [-1, 1] ── */

  noise3D(x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = this._fade(xf), v = this._fade(yf), w = this._fade(zf);

    const aaa = this._grad(this._hash(xi,     yi,     zi    ), xf,     yf,     zf    );
    const baa = this._grad(this._hash(xi + 1, yi,     zi    ), xf - 1, yf,     zf    );
    const aba = this._grad(this._hash(xi,     yi + 1, zi    ), xf,     yf - 1, zf    );
    const bba = this._grad(this._hash(xi + 1, yi + 1, zi    ), xf - 1, yf - 1, zf    );
    const aab = this._grad(this._hash(xi,     yi,     zi + 1), xf,     yf,     zf - 1);
    const bab = this._grad(this._hash(xi + 1, yi,     zi + 1), xf - 1, yf,     zf - 1);
    const abb = this._grad(this._hash(xi,     yi + 1, zi + 1), xf,     yf - 1, zf - 1);
    const bbb = this._grad(this._hash(xi + 1, yi + 1, zi + 1), xf - 1, yf - 1, zf - 1);

    const la = (a, b, t) => a + t * (b - a);
    return la(
      la(la(aaa, baa, u), la(aba, bba, u), v),
      la(la(aab, bab, u), la(abb, bbb, u), v),
      w
    );
  }

  /* ── Fractal Brownian Motion ──────────────────── */

  fbm(x, y, z, octaves = 6, lacunarity = 2.0, gain = 0.5) {
    let sum = 0, amp = 1, freq = 1, maxAmp = 0;
    for (let i = 0; i < octaves; i++) {
      sum += this.noise3D(x * freq, y * freq, z * freq) * amp;
      maxAmp += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    return sum / maxAmp;
  }

  /* ── Ridge noise — sharp mountain ridges ──────── */

  ridge(x, y, z, octaves = 6, lacunarity = 2.0, gain = 0.5, sharpness = 2.0) {
    let sum = 0, amp = 1, freq = 1, maxAmp = 0, prev = 1;
    for (let i = 0; i < octaves; i++) {
      let n = this.noise3D(x * freq, y * freq, z * freq);
      n = 1.0 - Math.abs(n);          // fold → ridges
      n = Math.pow(n, sharpness);      // sharpen
      n *= prev;                       // cascade
      prev = n;
      sum += n * amp;
      maxAmp += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    return sum / maxAmp;
  }

  /* ── Billow noise — rounded, puffy hills ──────── */

  billow(x, y, z, octaves = 6, lacunarity = 2.0, gain = 0.5) {
    let sum = 0, amp = 1, freq = 1, maxAmp = 0;
    for (let i = 0; i < octaves; i++) {
      let n = this.noise3D(x * freq, y * freq, z * freq);
      n = Math.abs(n) * 2 - 1;
      sum += n * amp;
      maxAmp += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    return sum / maxAmp;
  }

  /* ── Domain-warped noise — organic swirls ─────── */

  warp(x, y, z, strength = 0.5, octaves = 4) {
    const ox = this.fbm(x + 5.2,  y + 1.3,  z + 8.7,  octaves) * strength;
    const oy = this.fbm(x + 9.1,  y + 4.6,  z + 2.8,  octaves) * strength;
    const oz = this.fbm(x + 3.4,  y + 7.9,  z + 6.1,  octaves) * strength;
    return this.fbm(x + ox, y + oy, z + oz, octaves);
  }

  /* ── Cellular / Voronoi ── returns { f1, f2, edge } */

  cellular(x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    let minDist = 999, secondDist = 999;

    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const cx = xi + dx, cy = yi + dy, cz = zi + dz;
          const h = this._hash(cx, cy, cz);
          const fx = cx + (h & 0xff) / 255;
          const fy = cy + ((h >> 8) & 0xff) / 255;
          const fz = cz + ((h >> 16) & 0xff) / 255;
          const ddx = x - fx, ddy = y - fy, ddz = z - fz;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz);
          if (dist < minDist) { secondDist = minDist; minDist = dist; }
          else if (dist < secondDist) { secondDist = dist; }
        }
      }
    }

    return { f1: minDist, f2: secondDist, edge: secondDist - minDist };
  }
}
